import { addDays, dayLabel, daysBetween, todayStr } from "./date";
import { older } from "./eating";
import type { Insight } from "./insight";
import type { DayLog, DoseStep, HowWas, JointKey, KayaState, PhaseIndex, Programme, SessionKey } from "./types";

/**
 * "How was it?" and what it does to the next session (ADR-054; plan 2026-10-05 §3.4;
 * `plans-by-age-sex-goal.md` §5.2, §5.3; `older-adults-guidelines.md` §5).
 *
 * Two kinds of rule, labelled as such. Otago's: a strength move goes to its next level after two
 * sets of ten done well. GLA:D's: pain up to 5 of 10 that settles is acceptable. Everything else
 * — two easy sessions before a step, two hard before a step down, a week without a session as a
 * step back, the 28-day pain window — is Kaya's own; no guideline gives those figures.
 *
 * The step is a per-template dose index (`profile.training.doseStep`): 0 as written, 1 one
 * variable longer (time), 2 the next (reps); past 2 a move goes to its harder variant only where
 * Otago's rule holds. P2's `fillTemplate` reads `doseStepFor` to pick the dose tuple, and
 * `painRule` for the joints whose moves go one variant easier or out.
 */

/** Otago: two sets of ten done well before a move's next level (Otago manual p. 16). */
export const OTAGO = { sets: 2, reps: 10 } as const;

export function otagoReady(sets: number, reps: number): boolean {
  return sets >= OTAGO.sets && reps >= OTAGO.reps;
}

/** GLA:D: pain up to 5 of 10 during exercise is acceptable if it settles within a day. */
export const PAIN_LIMIT = 5;
/** Kaya's own: a second report over the limit within this many days swaps the joint's moves out. */
export const PAIN_WINDOW_DAYS = 28;
/** Kaya's own: a gap longer than this with no session steps a Steady person back. */
export const MISSED_WEEK_DAYS = 7;

export const DOSE_MAX: DoseStep = 2;
/** What each step adds, in the order the plan gives: time, then reps, then the harder variant. */
export const STEP_VARIABLE: Record<DoseStep, string> = { 0: "as written", 1: "longer holds and rounds", 2: "more reps" };

export function programmeOf(S: KayaState, today = new Date()): Programme {
  return S.profile.training?.programme ?? (older(S, today) ? "steady" : "kaya");
}

/* ---------- the answers, as a history ---------- */

export interface Feedback {
  ds: string;
  /** the template it was asked after: the answer's own key, else what the day ran */
  key: SessionKey | null;
  fit: HowWas["fit"];
  pain?: HowWas["pain"];
  /** the talk test, when it was asked (ADR-065) */
  talk?: HowWas["talk"];
  short: boolean;
  at: number;
}

/** The talk test's three answers (ADR-065): words, never a grade. */
export const TALK_WORDS: Record<NonNullable<HowWas["talk"]>, string> = {
  easy: "Could talk easily",
  talk: "Could talk, not sing",
  hard: "Hard to talk",
};

/** What the dose rule reads: "Hard to talk" counts as "Too hard" (ADR-065); the phase pin reads
 *  `fit` alone, so a breathless walk never moves a Kaya person's phase. */
function doseFit(f: Feedback): HowWas["fit"] {
  return f.talk === "hard" ? "hard" : f.fit;
}

function keyOf(L: DayLog, h: HowWas): SessionKey | null {
  return h.key ?? L.ran?.key ?? L.sessionKey ?? null;
}

/** Every "How was it?" answer, oldest first. */
export function feedback(S: KayaState): Feedback[] {
  const out: Feedback[] = [];
  for (const [ds, L] of Object.entries(S.logs)) {
    const h = L.howWas;
    if (!h) continue;
    const key = keyOf(L, h);
    // One fact, one source: what the day froze says whether it ran short (DayRan.short, ADR-052);
    // `howWas.short` is its mirror, read only for a day with no frozen block of that template.
    const short = L.ran && L.ran.key === key ? !!L.ran.short : !!h.short;
    out.push({ ds, key, fit: h.fit, pain: h.pain, ...(h.talk ? { talk: h.talk } : {}), short, at: h.at });
  }
  return out.sort((a, b) => (a.ds === b.ds ? a.at - b.at : a.ds < b.ds ? -1 : 1));
}

/** Asked once a day: never again once the day has an answer (plan §3.4). */
export function askHowWas(S: KayaState, ds: string): boolean {
  return !S.logs[ds]?.howWas;
}

/* ---------- a missed week ---------- */

function trained(L: DayLog | undefined): boolean {
  // a rescue is a session too: the first one back may be one (ADR-065)
  return !!L && (!!L.completedAt || !!L.howWas || !!L.rescued || !!L.rescueDone);
}

/** The last day before `today` with a finished session, or null. */
export function lastSessionBefore(S: KayaState, today: string): string | null {
  let best: string | null = null;
  for (const [ds, L] of Object.entries(S.logs)) if (ds < today && trained(L) && (!best || ds > best)) best = ds;
  return best;
}

/**
 * No session in the seven days before today, after at least one (research §6 #10: "do not
 * progress by date alone"). A first session ever is not a gap.
 */
export function missedWeek(S: KayaState, today: string): boolean {
  const last = lastSessionBefore(S, today);
  return last !== null && daysBetween(last, today) > MISSED_WEEK_DAYS;
}

/* ---------- the dose step ---------- */

function stored(S: KayaState, key: SessionKey): DoseStep {
  return S.profile.training?.doseStep?.[key] ?? 0;
}

/**
 * The dose index a template runs at today — what P2's `fillTemplate` must read. A Steady person
 * back after a week with no session runs one step lower (Kaya's rule); the step is written back
 * when that day's answer is saved (`nextDose`), so it is taken once.
 */
export function doseStepFor(S: KayaState, key: SessionKey, today = todayStr(), now = new Date()): DoseStep {
  const s = stored(S, key);
  if (programmeOf(S, now) === "steady" && missedWeek(S, today) && s > 0) return (s - 1) as DoseStep;
  return s;
}

export interface DoseDecision {
  key: SessionKey;
  from: DoseStep;
  step: DoseStep;
  change: "up" | "down" | "same";
  /** at the top step, easy twice more: the moves whose dose meets Otago's 2 × 10 go to their harder variant */
  variant: boolean;
  /** three "About right" in a row: a step up is offered, never imposed (§5.2) */
  offer: boolean;
  /** one sentence: why the next session is what it is */
  why: string;
}

/**
 * The next session's dose for a template from the answers since its step last moved (the table in
 * ADR-054): easy twice → one step up, a Short version never counts towards it; hard once → the same
 * again; hard twice → one step down; a week with no session → one step back first. `today` is the
 * day of the answer just saved.
 */
export function nextDose(S: KayaState, key: SessionKey, today = todayStr(), now = new Date()): DoseDecision {
  const was = stored(S, key);
  const missed = programmeOf(S, now) === "steady" && missedWeek(S, today) && was > 0;
  const base = doseStepFor(S, key, today, now);
  // after a step moved, the run starts again; after a missed week, it starts today
  const since = missed ? new Date(`${today}T00:00:00`).getTime() - 1 : (S.profile.training?.doseAt?.[key] ?? 0);
  const run = feedback(S).filter((f) => f.key === key && f.at > since);
  const last2 = run.slice(-2);
  const last3 = run.slice(-3);
  const out: DoseDecision = { key, from: was, step: base, change: base < was ? "down" : "same", variant: false, offer: false, why: "" };

  if (last2.length === 2 && last2.every((f) => doseFit(f) === "easy" && !f.short)) {
    if (base < DOSE_MAX) {
      out.step = (base + 1) as DoseStep;
      out.change = "up";
      out.why = `Too easy twice: next time, ${STEP_VARIABLE[out.step]}.`;
    } else {
      out.variant = true;
      out.why = "Too easy twice at the top step: moves done as two sets of ten go to their harder version.";
    }
  } else if (last2.length === 2 && last2.every((f) => doseFit(f) === "hard")) {
    out.step = Math.max(0, base - 1) as DoseStep;
    out.change = out.step < was ? "down" : "same";
    const what = last2.every((f) => f.fit !== "hard") ? "Hard to talk" : "Too hard";
    out.why = base > 0 ? `${what} twice: next time, one step easier.` : `${what} twice: this is already the gentlest dose.`;
  } else if (run.length && doseFit(run[run.length - 1]) === "hard") {
    out.why = run[run.length - 1].fit === "hard" ? "Too hard: the same again next time." : "Hard to talk: the same again next time.";
  } else if (last3.length === 3 && last3.every((f) => doseFit(f) === "right") && base < DOSE_MAX) {
    out.offer = true;
    out.why = "About right three times: a step up is there if you want it.";
  } else if (missed) {
    out.why = "A week without a session: one step back to start again.";
  } else {
    out.why = "The same again next time.";
  }
  if (missed && out.change !== "up" && !out.why.startsWith("A week"))
    out.why = `A week without a session, so one step back. ${out.why}`;
  return out;
}

/* ---------- pain ---------- */

export interface JointRule {
  level: "easier" | "out";
  /** the highest score in the window */
  score: number;
  line: string;
}

const JOINT_WORDS: Record<JointKey, string> = {
  neck: "neck",
  shoulder: "shoulder",
  back: "back",
  hip: "hip",
  knee: "knee",
  ankle: "ankle or foot",
  wrist: "wrist",
};

/** "knee", "ankle or foot" — a joint in a sentence. */
export function jointWord(j: JointKey): string {
  return JOINT_WORDS[j];
}

interface PainDay {
  ds: string;
  score: number;
  /** counted only because the morning after said "Still sore" */
  morning: boolean;
}

/**
 * The joints whose moves change next time (§5.2): pain over 5 once in the last 28 days → that
 * joint's moves one variant easier; a second time → swapped out, with "see a physiotherapist".
 * "Still sore" the next morning is the other half of Silbernagel's rule (ADR-065): it counts that
 * session as over the limit for the joint whatever its score, and a session is counted once.
 */
export function painRule(S: KayaState, today = todayStr()): Partial<Record<JointKey, JointRule>> {
  const from = addDays(today, -(PAIN_WINDOW_DAYS - 1));
  const out: Partial<Record<JointKey, JointRule>> = {};
  const days: Partial<Record<JointKey, Map<string, PainDay>>> = {};
  const add = (j: JointKey, d: PainDay) => {
    const m = (days[j] ??= new Map());
    const was = m.get(d.ds);
    // a score over the limit is the stronger fact: its own line, with the figure
    if (!was || (was.morning && !d.morning)) m.set(d.ds, d);
  };
  for (const f of feedback(S)) {
    if (f.ds < from || f.ds > today || !f.pain || f.pain.score <= PAIN_LIMIT) continue;
    add(f.pain.joint, { ds: f.ds, score: f.pain.score, morning: false });
  }
  for (const L of Object.values(S.logs)) {
    const m = L.morning;
    if (!m || m.ok || m.ds < from || m.ds > today) continue;
    const said = S.logs[m.ds]?.howWas?.pain;
    add(m.joint, { ds: m.ds, score: said && said.joint === m.joint ? said.score : 0, morning: true });
  }
  for (const [j, m] of Object.entries(days) as [JointKey, Map<string, PainDay>][]) {
    const list = Array.from(m.values()).sort((x, y) => (x.ds < y.ds ? -1 : 1));
    const w = JOINT_WORDS[j];
    const score = Math.max(...list.map((d) => d.score));
    const last = list[list.length - 1];
    if (list.length >= 2) {
      out[j] = {
        level: "out",
        score,
        line: list.every((d) => d.morning)
          ? `Your ${w} was still sore the morning after two sessions: its moves are left out for now. See a physiotherapist before they come back.`
          : `Your ${w} hurt after two sessions: its moves are left out for now. See a physiotherapist before they come back.`,
      };
    } else {
      out[j] = {
        level: "easier",
        score,
        line: last.morning
          ? `Your ${w} was still sore the morning after ${dayLabel(last.ds)}: its moves are one step easier next time.`
          : `Your ${w} hurt (${last.score} of 10) on ${dayLabel(last.ds)}: its moves are one step easier next time.`,
      };
    }
  }
  return out;
}

/* ---------- the morning after (ADR-065) ---------- */

export const MORNING_WORDS = { title: "This morning", ok: "Back to normal", sore: "Still sore" } as const;

export interface MorningAsk {
  /** the session day the pain was reported on — yesterday */
  ds: string;
  joint: JointKey;
  score: number;
}

/** Yesterday's "Anything hurt?" with a score of 1 or more, asked once, on today only. */
export function morningAsk(S: KayaState, today = todayStr()): MorningAsk | null {
  const ds = addDays(today, -1);
  const pain = S.logs[ds]?.howWas?.pain;
  if (!pain || pain.score < 1 || S.logs[today]?.morning) return null;
  return { ds, joint: pain.joint, score: pain.score };
}

/** Today's notice: "Yesterday your knee hurt (4 of 10). How is it now?" */
export function morningAskLine(a: MorningAsk): string {
  return `Yesterday your ${JOINT_WORDS[a.joint]} hurt (${a.score} of 10). How is it now?`;
}

/** Train's hero sub for a Kaya person whose morning said "Still sore"; the Steady fill already
 *  reacts through `painRule`. */
export function morningLine(S: KayaState, today = todayStr()): string | null {
  const m = S.logs[today]?.morning;
  return m && !m.ok ? "Still sore this morning — go easier today." : null;
}

/* ---------- the first one back (ADR-065) ---------- */

/** Days since the last session when more than a week has gone by and today is not yet trained;
 *  null otherwise, and for a first session ever. */
export function backAfterBreak(S: KayaState, today = todayStr()): number | null {
  if (trained(S.logs[today])) return null;
  const last = lastSessionBefore(S, today);
  if (!last) return null;
  const days = daysBetween(last, today);
  return days > MISSED_WEEK_DAYS ? days : null;
}

/** Steady: the Short version is the session; Kaya: an offer (ADR-065). */
export function backLine(days: number, steady: boolean): string {
  return steady
    ? `First one back after ${days} days: the Short version today, and it counts.`
    : `First one back after ${days} days — go easy today.`;
}

/* ---------- the talk test (ADR-065) ---------- */

/** The talk test is asked after a walk, a treadmill run, or a walk-day session — the effort it
 *  measures is the walking. `key` is the session just finished, else what the day ran. */
export function askTalk(S: KayaState, ds: string, key?: SessionKey): boolean {
  const L = S.logs[ds];
  if (L?.walks?.length || L?.treadmill) return true;
  const k = key ?? L?.ran?.key ?? L?.sessionKey;
  return k === "WK" || (k === "MJ" && (S.profile.training?.mealWalks?.length ?? 0) > 0);
}

const COUNT_WORDS = ["", "one", "two", "three", "four", "five"];

/** Progress › Training: the last five talk-test answers in words, and what they suggest. Null
 *  under three answers. */
export function talkInsight(S: KayaState, today = todayStr()): Insight | null {
  const all = feedback(S).filter((f) => f.talk && f.ds <= today);
  const last = all.slice(-5);
  if (last.length < 3) return null;
  const n = last.length;
  const count = (t: NonNullable<HowWas["talk"]>) => last.filter((f) => f.talk === t).length;
  const c = { easy: count("easy"), talk: count("talk"), hard: count("hard") };
  const parts = (["easy", "talk", "hard"] as const)
    .filter((t) => c[t] > 0)
    .map((t) => `${TALK_WORDS[t].toLowerCase()} on ${c[t]}`);
  const tail =
    c.hard >= 2
      ? "ease the brisk stretches a little."
      : c.easy * 2 > n
        ? "a quicker step would make them count as brisk."
        : "that is the brisk effort the walks are for.";
  return {
    key: "training.talk",
    text: `On your last ${COUNT_WORDS[n]} walks: ${parts.join(", ")} — ${tail}`,
    basedOn: `Could you talk? on How was it?, your last ${COUNT_WORDS[n]} answers`,
  };
}

/* ---------- the Kaya programme: the calendar stays, "too hard" twice pins a phase ---------- */

export interface PhasePinDecision {
  from: PhaseIndex;
  to: PhaseIndex;
}

/**
 * On the Kaya programme the calendar phase stays (plan §3.4), but "Too hard" twice in a row, on
 * any session since the last pin, pins `profile.phaseMode` one lower. `current` is
 * `plan.ts::phase(S)`. Null at phase 1 (index 0), on the Steady programme, or without two.
 */
export function phasePinFor(S: KayaState, current: PhaseIndex, now = new Date()): PhasePinDecision | null {
  if (programmeOf(S, now) !== "kaya" || current === 0) return null;
  const since = S.profile.training?.phasePin?.at ?? 0;
  const run = feedback(S).filter((f) => f.at > since);
  const last2 = run.slice(-2);
  if (last2.length < 2 || !last2.every((f) => f.fit === "hard")) return null;
  return { from: current, to: (current - 1) as PhaseIndex };
}

/** Train's Notice while the pin stands; gone once the phase is changed back in You › Sessions. */
export function phasePinLine(S: KayaState): string | null {
  const pin = S.profile.training?.phasePin;
  if (!pin || S.profile.phaseMode !== String(pin.to)) return null;
  return `Too hard twice, so Kaya holds you at phase ${pin.to + 1} instead of ${pin.from + 1}. Change it back in You › Sessions.`;
}
